import { Controller } from "@hotwired/stimulus";

// Builder de template d'audit. Toute la page est pilotée ici :
//   - Update du nom/description du template (PATCH AJAX)
//   - CRUD des sections (POST/PATCH/DELETE AJAX) avec re-render léger
//   - CRUD des questions (idem)
//   - Toggle obligatoire/facultatif
//   - Action sur réponse négative
//   - Options pour les types à choix (saisies en plain-text, 1 par ligne)
export default class extends Controller {
  static targets = ["toc", "tocList", "content", "nameInput", "descInput",
                    "sectionWrapper", "emptyState"];
  static values = {
    templateId: Number,
    templateUrl: String,
    sectionsUrl: String,
    questionsUrl: String,
    reorderSectionsUrl: String,
    reorderQuestionsUrl: String,
    readOnly: Boolean,
  };

  connect() {
    this._csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  }

  // ── Header (template name + description) ─────────────────────────────

  async updateTemplate() {
    if (this.readOnlyValue) return;
    const body = new FormData();
    body.append("audit_template[nom]", this.nameInputTarget.value);
    body.append("audit_template[description]", this.descInputTarget.value);
    await this._fetch(this.templateUrlValue, { method: "PATCH", body });
  }

  // ── Sections ─────────────────────────────────────────────────────────

  async addSection(event) {
    if (this.readOnlyValue) return;
    const scope = event.currentTarget.dataset.scope;
    const body = new FormData();
    body.append("audit_template_section[scope]", scope);
    body.append("audit_template_section[nom]", "Nouvelle section");
    const res = await this._fetchJson(this.sectionsUrlValue, { method: "POST", body });
    if (!res?.ok) return this._flash("error", res?.errors?.join(", ") || "Création impossible.");
    this._appendSectionToToc(res.section);
    // Recharge le DOM complet pour avoir le nouveau wrapper section
    await this._reloadSectionWrapper(res.section.id, { showAfter: true });
  }

  async updateSection(event) {
    if (this.readOnlyValue) return;
    const id = event.currentTarget.dataset.sectionId;
    const field = event.currentTarget.dataset.field;
    const value = event.currentTarget.value;
    const body = new FormData();
    body.append(`audit_template_section[${field}]`, value);
    const res = await this._fetchJson(`${this.sectionsUrlValue}/${id}`, { method: "PATCH", body });
    if (!res?.ok) return this._flash("error", res?.errors?.join(", ") || "Sauvegarde impossible.");
    if (field === "nom") this._updateTocLabel(id, value);
  }

  async deleteSection(event) {
    if (this.readOnlyValue) return;
    if (!confirm("Supprimer cette section et toutes ses questions ?")) return;
    const id = event.currentTarget.dataset.sectionId;
    const res = await this._fetchJson(`${this.sectionsUrlValue}/${id}`, { method: "DELETE" });
    if (!res?.ok) return;
    this._removeTocItem(id);
    // Retire le wrapper de la section supprimée
    const wrapper = this.contentTarget.querySelector(`[data-section-id="${id}"]`);
    wrapper?.remove();
    // Affiche la 1re section restante, ou l'empty state
    const next = this.sectionWrapperTargets[0];
    if (next) {
      this._showSection(next.dataset.sectionId);
      this._highlightTocItem(next.dataset.sectionId);
    } else if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.hidden = false;
    }
  }

  openSection(event) {
    const id = event.currentTarget.dataset.sectionId;
    this._showSection(id);
    this._highlightTocItem(id);
  }

  // Toggle local : on cache toutes les sections sauf celle ciblée.
  _showSection(sectionId) {
    this.sectionWrapperTargets.forEach(wrapper => {
      wrapper.hidden = wrapper.dataset.sectionId !== String(sectionId);
    });
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = true;
  }

  // ── Questions ────────────────────────────────────────────────────────

  async addQuestion(event) {
    if (this.readOnlyValue) return;
    const sectionId = event.currentTarget.dataset.sectionId;
    const body = new FormData();
    body.append("audit_template_question[audit_template_section_id]", sectionId);
    body.append("audit_template_question[label]", "Nouvelle question");
    body.append("audit_template_question[question_type]", "yes_no");
    body.append("audit_template_question[required]", "false");
    const res = await this._fetchJson(this.questionsUrlValue, { method: "POST", body });
    if (!res?.ok) return this._flash("error", res?.errors?.join(", ") || "Création impossible.");
    await this._reloadSectionWrapper(sectionId);
    this._bumpTocCount(sectionId, +1);
  }

  async updateQuestion(event) {
    if (this.readOnlyValue) return;
    const id = event.currentTarget.dataset.questionId;
    const field = event.currentTarget.dataset.field;
    const value = event.currentTarget.value;
    const body = new FormData();
    body.append(`audit_template_question[${field}]`, value);
    const res = await this._fetchJson(`${this.questionsUrlValue}/${id}`, { method: "PATCH", body });
    if (!res?.ok) return this._flash("error", res?.errors?.join(", ") || "Sauvegarde impossible.");
    // Si on a changé le type, on doit re-render la question pour faire
    // apparaître/disparaître les options ou l'action sur Non.
    if (field === "question_type") {
      const sectionId = event.currentTarget.closest("[data-section-id]")?.dataset.sectionId;
      if (sectionId) await this._reloadSectionWrapper(sectionId);
    }
  }

  async updateQuestionToggle(event) {
    if (this.readOnlyValue) return;
    const id = event.currentTarget.dataset.questionId;
    const field = event.currentTarget.dataset.field;
    const value = event.currentTarget.checked ? "true" : "false";
    const body = new FormData();
    body.append(`audit_template_question[${field}]`, value);
    const res = await this._fetchJson(`${this.questionsUrlValue}/${id}`, { method: "PATCH", body });
    if (!res?.ok) return;
    const sectionId = event.currentTarget.closest("[data-section-id]")?.dataset.sectionId;
    if (sectionId) await this._renderSection(sectionId);
  }

  async updateOptions(event) {
    if (this.readOnlyValue) return;
    const id = event.currentTarget.dataset.questionId;
    const lines = event.currentTarget.value
      .split("\n").map(l => l.trim()).filter(Boolean);
    const opts = lines.map(l => ({ label: l, value: l.toLowerCase().replace(/\s+/g, "_") }));
    const body = new FormData();
    body.append("audit_template_question[options]", JSON.stringify(opts));
    await this._fetchJson(`${this.questionsUrlValue}/${id}`, { method: "PATCH", body });
  }

  async updateNegativeAction(event) {
    if (this.readOnlyValue) return;
    const id = event.currentTarget.dataset.questionId;
    const action = event.currentTarget.value;
    const body = new FormData();
    body.append("audit_template_question[on_negative_action]",
               JSON.stringify({ action }));
    await this._fetchJson(`${this.questionsUrlValue}/${id}`, { method: "PATCH", body });
  }

  async deleteQuestion(event) {
    if (this.readOnlyValue) return;
    if (!confirm("Supprimer cette question ?")) return;
    const id = event.currentTarget.dataset.questionId;
    const sectionId = event.currentTarget.closest("[data-section-id]")?.dataset.sectionId;
    const res = await this._fetchJson(`${this.questionsUrlValue}/${id}`, { method: "DELETE" });
    if (!res?.ok) return;
    if (sectionId) {
      await this._reloadSectionWrapper(sectionId);
      this._bumpTocCount(sectionId, -1);
    }
  }

  // ── Internals ────────────────────────────────────────────────────────

  // Recharge un seul wrapper de section depuis le serveur (utile après
  // ajout/suppression/edit de question pour récupérer le HTML à jour).
  // Si le wrapper n'existe pas dans le DOM, on l'ajoute.
  async _reloadSectionWrapper(sectionId, { showAfter = false } = {}) {
    const res = await fetch(`${this.templateUrlValue}/edit`, {
      headers: { "Accept": "text/html" },
    });
    if (!res.ok) return;
    const html = await res.text();
    const dom = new DOMParser().parseFromString(html, "text/html");
    const fresh = dom.querySelector(
      `.audb-section-wrapper[data-section-id="${sectionId}"]`
    );
    if (!fresh) return;

    const existing = this.contentTarget.querySelector(
      `.audb-section-wrapper[data-section-id="${sectionId}"]`
    );
    if (existing) {
      // Préserve l'état de visibilité actuel
      const wasHidden = existing.hidden;
      existing.replaceWith(fresh);
      fresh.hidden = wasHidden && !showAfter;
    } else {
      this.contentTarget.appendChild(fresh);
      fresh.hidden = !showAfter;
    }
    if (showAfter) this._showSection(sectionId);
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = true;
  }

  _appendSectionToToc(section) {
    const list = this.tocListTargets.find(ul => ul.dataset.scope === section.scope);
    if (!list) return;
    const li = document.createElement("li");
    li.className = "audb-toc__item";
    li.dataset.sectionId = section.id;
    li.innerHTML = `
      <button type="button" class="audb-toc__link"
              data-action="click->audit-template-builder#openSection"
              data-section-id="${section.id}">
        <span class="audb-toc__name">${this._esc(section.nom)}</span>
        <span class="audb-toc__count">0</span>
      </button>`;
    list.appendChild(li);
  }

  _updateTocLabel(sectionId, name) {
    const item = this.tocTarget.querySelector(`[data-section-id="${sectionId}"] .audb-toc__name`);
    if (item) item.textContent = name;
  }

  _bumpTocCount(sectionId, delta) {
    const el = this.tocTarget.querySelector(`[data-section-id="${sectionId}"] .audb-toc__count`);
    if (!el) return;
    const cur = parseInt(el.textContent || "0", 10);
    el.textContent = String(Math.max(0, cur + delta));
  }

  _removeTocItem(sectionId) {
    const item = this.tocTarget.querySelector(`[data-section-id="${sectionId}"]`);
    item?.remove();
  }

  _highlightTocItem(sectionId) {
    this.tocTarget.querySelectorAll(".audb-toc__item--active")
        .forEach(el => el.classList.remove("audb-toc__item--active"));
    const target = this.tocTarget.querySelector(`[data-section-id="${sectionId}"]`);
    target?.classList.add("audb-toc__item--active");
  }

  _emptyState() {
    return `<div class="audb-empty">
      <i class="fa-solid fa-inbox"></i>
      <h2>Pas de section ouverte</h2>
      <p>Sélectionne une section dans la colonne de gauche.</p>
    </div>`;
  }

  async _fetchJson(url, options) {
    try {
      const res = await this._fetch(url, options);
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("application/json")) return null;
      return await res.json();
    } catch (_) { return null; }
  }

  async _fetch(url, options) {
    const opts = { ...options, headers: { ...(options.headers || {}), "X-CSRF-Token": this._csrf, "Accept": "application/json" } };
    return fetch(url, opts);
  }

  _flash(_kind, msg) {
    if (msg) console.warn("[audit-builder]", msg);
  }

  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;").replaceAll("'", "&#39;");
  }
}
